import * as React from 'react';
import { Virtuoso } from 'react-virtuoso';
import { IoChevronForward, IoChevronDown } from 'react-icons/io5';
import Link from 'components/Link';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import { HeatScore } from 'components/Score';
import HeatScoreBadge from 'components/Score/HeatScoreBadge';
import {
  TableBody,
  TableCell,
  TableCellSmall,
  TableCellNoPad,
  TableHeader,
  HeaderCellWrapper,
  HeaderCell,
  HeaderIcon,
  IconSortDesc,
  IconSortAsc,
  CallPutText,
  CallPutBackground,
  MutedText,
} from 'components/Table';
import { Trade } from 'utils/shared/trades/types';
import { SortDirection, ColumnType } from 'utils/shared/trades/types';
import Pulse from 'components/Loading/Pulse';
import { ChainRow } from 'utils/shared/trades/groupTradesIntoChain';
import type { FlowMetricMode } from 'utils/shared/trades/presets';
import { TICKER_FLOW_PAGE } from 'constants/pages';
import MobileChainCard from 'components/MobileChainCard';

import {
  ChainDashboardRow,
  ChildTableWrapper,
  ChildHeaderRow,
  ChildHeaderCell,
  ChildRow,
  ChevronButton,
  PARENT_COLUMNS_SINGLE,
  PARENT_COLUMNS_MULTI,
  PARENT_COLUMNS_MULTI_NO_DATE,
  CHILD_COLUMNS_SINGLE,
  CHILD_COLUMNS_MULTI,
} from './styles';

export interface ChainSortState {
  sortKey: string;
  sortDirection: SortDirection;
  handleSort: (headerSortKey: string) => void;
}

interface Props {
  chainRows: ChainRow[];
  expandAllTrigger?: number;
  collapseAllTrigger?: number;
  onExpandStateChange?: (allExpanded: boolean, hasRows: boolean) => void;
  /** Include ticker column (for multi-ticker pages) */
  includeTicker?: boolean;
  /** Hide the date column (e.g. for realtime flow page) */
  hideDate?: boolean;
  /** When true, skip rendering TableHeader — parent renders the sort headers */
  headerless?: boolean;
  /** Show mobile card layout instead of table */
  isMobile?: boolean;
  /** Callback to expose sort state to parent (for headerless mode) */
  onSortStateChange?: (state: ChainSortState) => void;
  /** When explicitly false and there are no rows, show an empty state instead of Pulse. */
  isLoading?: boolean;
  metricMode?: FlowMetricMode;
}

export const CHAIN_TABLE_HEADERS_SINGLE = [
  { name: '', sortKey: 'expand', columnType: ColumnType.Text },
  { name: 'Date', sortKey: 'date', columnType: ColumnType.Date },
  { name: 'Chain', sortKey: 'chainLabel', columnType: ColumnType.Text },
  { name: 'Total Trades', sortKey: 'count', columnType: ColumnType.Number },
  { name: 'Avg Spot', sortKey: 'avgSpot', columnType: ColumnType.Number },
  { name: 'Avg OTM', sortKey: 'avgOtmPercent', columnType: ColumnType.Number },
  { name: 'Avg Price', sortKey: 'avgPrice', columnType: ColumnType.Number },
  { name: 'Total Size', sortKey: 'totalSize', columnType: ColumnType.Number },
  { name: 'Open Interest', sortKey: 'openInterest', columnType: ColumnType.Number },
  { name: 'Avg IV', sortKey: 'avgImpliedVol', columnType: ColumnType.Number },
  { name: 'Total Premium', sortKey: 'totalPremium', columnType: ColumnType.Number },
  { name: 'Avg Heat', sortKey: 'avgHeatScore', columnType: ColumnType.Number },
];

export const CHAIN_TABLE_HEADERS_MULTI = [
  { name: '', sortKey: 'expand', columnType: ColumnType.Text },
  { name: 'Date', sortKey: 'date', columnType: ColumnType.Date },
  { name: 'Ticker', sortKey: 'ticker', columnType: ColumnType.Text },
  { name: 'Chain', sortKey: 'chainLabel', columnType: ColumnType.Text },
  { name: 'Trades', sortKey: 'count', columnType: ColumnType.Number },
  { name: 'Avg Spot', sortKey: 'avgSpot', columnType: ColumnType.Number },
  { name: 'Avg OTM', sortKey: 'avgOtmPercent', columnType: ColumnType.Number },
  { name: 'Total Size', sortKey: 'totalSize', columnType: ColumnType.Number },
  { name: 'OI', sortKey: 'openInterest', columnType: ColumnType.Number },
  { name: 'Avg IV', sortKey: 'avgImpliedVol', columnType: ColumnType.Number },
  { name: 'Total Premium', sortKey: 'totalPremium', columnType: ColumnType.Number },
  { name: 'Avg Heat', sortKey: 'avgHeatScore', columnType: ColumnType.Number },
];

export const CHAIN_TABLE_HEADERS_MULTI_NO_DATE = [
  { name: '', sortKey: 'expand', columnType: ColumnType.Text },
  { name: 'Ticker', sortKey: 'ticker', columnType: ColumnType.Text },
  { name: 'Chain', sortKey: 'chainLabel', columnType: ColumnType.Text },
  { name: 'Trades', sortKey: 'count', columnType: ColumnType.Number },
  { name: 'Avg Spot', sortKey: 'avgSpot', columnType: ColumnType.Number },
  { name: 'Avg OTM', sortKey: 'avgOtmPercent', columnType: ColumnType.Number },
  { name: 'Total Size', sortKey: 'totalSize', columnType: ColumnType.Number },
  { name: 'OI', sortKey: 'openInterest', columnType: ColumnType.Number },
  { name: 'Avg IV', sortKey: 'avgImpliedVol', columnType: ColumnType.Number },
  { name: 'Total Premium', sortKey: 'totalPremium', columnType: ColumnType.Number },
  { name: 'Avg Heat', sortKey: 'avgHeatScore', columnType: ColumnType.Number },
];

const CHILD_HEADERS_SINGLE = [
  'Date',
  'Time',
  'C/P',
  'Expiry',
  'Spot',
  'Strike',
  'OTM',
  'Price',
  'Size',
  'OI',
  'IV',
  'Type',
  'Premium',
  'Heat',
];

const CHILD_HEADERS_MULTI = [
  'Date',
  'Time',
  'Ticker',
  'C/P',
  'Expiry',
  'Spot',
  'Strike',
  'OTM',
  'Price',
  'Size',
  'OI',
  'IV',
  'Type',
  'Premium',
  'Heat',
];

const ChildFlowRowSingle: React.FC<{ trade: Trade }> = React.memo(({ trade }) => (
  <ChildRow tableBodyRow $columns={CHILD_COLUMNS_SINGLE}>
    <TableCell>
      <MutedText>{trade.date}</MutedText>
    </TableCell>
    <TableCell>
      <MutedText>
        {trade.time} {trade.timeSubscript}
      </MutedText>
    </TableCell>
    <TableCell>{trade.cp}</TableCell>
    <TableCell>{`${trade.expireYear}-${trade.expireMonth}-${trade.expireDay}`}</TableCell>
    <TableCell>${trade.spot?.toFixed(2) ?? '—'}</TableCell>
    <TableCell>${trade.strike}</TableCell>
    <TableCell>{trade.otmPercent != null ? `${Math.round(trade.otmPercent)}%` : '—'}</TableCell>
    <TableCell>
      ${trade.price?.toFixed(2) ?? '—'}{' '}
      <span className="ml-1 text-gray-400">{trade.tradeDirection}</span>
    </TableCell>
    <TableCell>{trade.size?.toLocaleString()}</TableCell>
    <TableCell>{trade.openInterest?.toLocaleString()}</TableCell>
    <TableCell>
      {trade.impliedVol != null ? `${Math.round(trade.impliedVol * 100)}%` : '—'}
    </TableCell>
    <TableCellSmall>{trade.orderType}</TableCellSmall>
    <TableCell>${abbreviateNumber(trade.premium)}</TableCell>
    <TableCellNoPad>
      <HeatScore scoreWidth={trade.scoreWidth} />
    </TableCellNoPad>
  </ChildRow>
));

const ChildFlowRowMulti: React.FC<{ trade: Trade }> = React.memo(({ trade }) => (
  <ChildRow tableBodyRow $columns={CHILD_COLUMNS_MULTI}>
    <TableCell>
      <MutedText>{trade.date}</MutedText>
    </TableCell>
    <TableCell>
      <MutedText>
        {trade.time} {trade.timeSubscript}
      </MutedText>
    </TableCell>
    <TableCell>
      <Link href={`${TICKER_FLOW_PAGE}/${trade.ticker}`}>
        <CallPutText type={trade.cp || ''}>{trade.ticker}</CallPutText>
      </Link>
    </TableCell>
    <TableCell>{trade.cp}</TableCell>
    <TableCell>{`${trade.expireYear}-${trade.expireMonth}-${trade.expireDay}`}</TableCell>
    <TableCell>${trade.spot?.toFixed(2) ?? '—'}</TableCell>
    <TableCell>${trade.strike}</TableCell>
    <TableCell>{trade.otmPercent != null ? `${Math.round(trade.otmPercent)}%` : '—'}</TableCell>
    <TableCell>
      ${trade.price?.toFixed(2) ?? '—'}{' '}
      <span className="ml-1 text-gray-400">{trade.tradeDirection}</span>
    </TableCell>
    <TableCell>{trade.size?.toLocaleString()}</TableCell>
    <TableCell>{trade.openInterest?.toLocaleString()}</TableCell>
    <TableCell>
      {trade.impliedVol != null ? `${Math.round(trade.impliedVol * 100)}%` : '—'}
    </TableCell>
    <TableCellSmall>{trade.orderType}</TableCellSmall>
    <TableCell>${abbreviateNumber(trade.premium)}</TableCell>
    <TableCellNoPad>
      <HeatScore scoreWidth={trade.scoreWidth} />
    </TableCellNoPad>
  </ChildRow>
));

const ChildFlowHeaderSingle: React.FC = React.memo(() => (
  <ChildHeaderRow $columns={CHILD_COLUMNS_SINGLE}>
    {CHILD_HEADERS_SINGLE.map((name) => (
      <ChildHeaderCell key={name}>{name}</ChildHeaderCell>
    ))}
  </ChildHeaderRow>
));

const ChildFlowHeaderMulti: React.FC = React.memo(() => (
  <ChildHeaderRow $columns={CHILD_COLUMNS_MULTI}>
    {CHILD_HEADERS_MULTI.map((name) => (
      <ChildHeaderCell key={name}>{name}</ChildHeaderCell>
    ))}
  </ChildHeaderRow>
));

const VirtuosoScroller = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (props, ref) => (
    <div {...props} ref={ref} style={{ ...props.style, overflowX: 'hidden', overflowY: 'auto' }} />
  ),
);

export default function OptionChainTable({
  chainRows,
  expandAllTrigger,
  collapseAllTrigger,
  onExpandStateChange,
  includeTicker = false,
  hideDate = false,
  headerless = false,
  isMobile = false,
  onSortStateChange,
  isLoading,
  metricMode = 'total',
}: Props) {
  const defaultSortKey = metricMode === 'net' ? 'netPremium' : 'totalPremium';
  const defaultSortDirection = SortDirection.Desc;
  const [sortKey, setSortKey] = React.useState(defaultSortKey);
  const [sortDirection, setSortDirection] = React.useState(defaultSortDirection);
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(new Set());

  const headers = includeTicker
    ? hideDate
      ? CHAIN_TABLE_HEADERS_MULTI_NO_DATE
      : CHAIN_TABLE_HEADERS_MULTI
    : CHAIN_TABLE_HEADERS_SINGLE;
  const displayHeaders = React.useMemo(
    () =>
      headers.map((header) => {
        if (metricMode !== 'net') {
          return header;
        }

        if (header.sortKey === 'totalSize') {
          return { ...header, name: 'Net Size', sortKey: 'netSize' };
        }

        if (header.sortKey === 'totalPremium') {
          return { ...header, name: 'Net Premium', sortKey: 'netPremium' };
        }

        return header;
      }),
    [headers, metricMode],
  );

  const parentColumns = includeTicker
    ? hideDate
      ? PARENT_COLUMNS_MULTI_NO_DATE
      : PARENT_COLUMNS_MULTI
    : PARENT_COLUMNS_SINGLE;

  const ChildFlowRow = includeTicker ? ChildFlowRowMulti : ChildFlowRowSingle;
  const ChildFlowHeader = includeTicker ? ChildFlowHeaderMulti : ChildFlowHeaderSingle;

  React.useEffect(() => {
    setExpandedKeys(new Set());
  }, [chainRows]);

  const sortedRows = React.useMemo(() => {
    if (sortDirection === SortDirection.None) {
      const fallbackSortKey = metricMode === 'net' ? 'netPremium' : 'totalPremium';
      return [...chainRows].sort((a, b) => b[fallbackSortKey] - a[fallbackSortKey]);
    }

    return [...chainRows].sort((a, b) => {
      const aVal = a[sortKey as keyof ChainRow];
      const bVal = b[sortKey as keyof ChainRow];
      if (sortKey === 'expand') return 0;
      if (aVal === bVal) return 0;
      const direction = sortDirection === SortDirection.Asc ? 1 : -1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * direction;
      }
      return ((aVal as number) - (bVal as number)) * direction;
    });
  }, [chainRows, sortKey, sortDirection, metricMode]);

  React.useEffect(() => {
    if (expandAllTrigger && expandAllTrigger > 0 && sortedRows.length > 0) {
      setExpandedKeys(new Set(sortedRows.map((r) => r.key)));
    }
  }, [expandAllTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (collapseAllTrigger && collapseAllTrigger > 0) {
      setExpandedKeys(new Set());
    }
  }, [collapseAllTrigger]);

  const handleToggleExpand = React.useCallback((chainKey: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(chainKey)) {
        next.delete(chainKey);
      } else {
        next.add(chainKey);
      }
      return next;
    });
  }, []);

  const handleSort = React.useCallback(
    (headerSortKey: string) => {
      if (headerSortKey === 'expand') return;
      setExpandedKeys(new Set());
      if (sortKey !== headerSortKey) {
        setSortKey(headerSortKey);
        setSortDirection(SortDirection.Desc);
      } else if (sortDirection === SortDirection.Desc) {
        setSortDirection(SortDirection.Asc);
      } else if (sortDirection === SortDirection.Asc) {
        setSortKey(defaultSortKey);
        setSortDirection(SortDirection.None);
      } else {
        setSortDirection(SortDirection.Desc);
      }
    },
    [sortKey, sortDirection, defaultSortKey],
  );

  React.useEffect(() => {
    setSortKey(defaultSortKey);
    setSortDirection(defaultSortDirection);
  }, [defaultSortKey, defaultSortDirection]);

  // Expose sort state to parent for headerless mode
  React.useEffect(() => {
    onSortStateChange?.({ sortKey, sortDirection, handleSort });
  }, [sortKey, sortDirection, handleSort, onSortStateChange]);

  const allExpanded = sortedRows.length > 0 && expandedKeys.size === sortedRows.length;
  const hasRows = sortedRows.length > 0;

  React.useEffect(() => {
    onExpandStateChange?.(allExpanded, hasRows);
  }, [allExpanded, hasRows, onExpandStateChange]);

  // Mobile card layout
  if (isMobile) {
    return (
      <div className="flex h-full flex-col overflow-x-hidden">
        {isLoading ? (
          <div className="flex h-full w-full items-center justify-center">
            <Pulse />
          </div>
        ) : sortedRows.length > 0 ? (
          <Virtuoso
            data={sortedRows}
            overscan={400}
            itemContent={(_index, row) => (
              <MobileChainCard
                key={row.key}
                row={row}
                isExpanded={expandedKeys.has(row.key)}
                onToggle={handleToggleExpand}
                metricMode={metricMode}
              />
            )}
          />
        ) : isLoading === false ? (
          <div className="flex h-full w-full items-center justify-center text-sm text-gray-400">
            No trades available in the selected range.
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Pulse />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" style={{ minWidth: 860 }}>
      {!headerless && (
        <TableHeader style={{ paddingTop: 0, paddingBottom: 0 }}>
          <ChainDashboardRow removePad $columns={parentColumns}>
            {displayHeaders.map((t, i) => {
              if (t.sortKey === 'expand') {
                return (
                  <HeaderCellWrapper key="expand" removePad>
                    <HeaderCell>
                      <span className="cell-name" />
                    </HeaderCell>
                  </HeaderCellWrapper>
                );
              }
              const isActiveSort = t.sortKey === sortKey && sortDirection !== SortDirection.None;
              return (
                <HeaderCellWrapper
                  key={t.name}
                  removePad={i + 1 === headers.length}
                  isActiveSort={isActiveSort}
                  onClick={() => handleSort(t.sortKey)}
                >
                  <HeaderCell>
                    <span className="cell-name">{t.name}</span>
                    {isActiveSort && sortDirection === SortDirection.Desc && (
                      <HeaderIcon>
                        <IconSortDesc />
                      </HeaderIcon>
                    )}
                    {isActiveSort && sortDirection === SortDirection.Asc && (
                      <HeaderIcon>
                        <IconSortAsc />
                      </HeaderIcon>
                    )}
                  </HeaderCell>
                </HeaderCellWrapper>
              );
            })}
          </ChainDashboardRow>
        </TableHeader>
      )}

      <TableBody className="h-full w-full">
        <div className="h-full w-full">
          {isLoading ? (
            <div className="flex h-full w-full items-center justify-center">
              <Pulse />
            </div>
          ) : sortedRows.length > 0 ? (
            <Virtuoso
              data={sortedRows}
              overscan={400}
              components={{ Scroller: VirtuosoScroller }}
              itemContent={(_index, row) => {
                const isExpanded = expandedKeys.has(row.key);
                const displaySize = metricMode === 'net' ? row.netSize : row.totalSize;
                const displayPremium = metricMode === 'net' ? row.netPremium : row.totalPremium;
                return (
                  <div>
                    <ChainDashboardRow
                      tableBodyRow
                      isExpanded={isExpanded}
                      $columns={parentColumns}
                    >
                      <TableCell>
                        <ChevronButton
                          onClick={() => handleToggleExpand(row.key)}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? 'Collapse chain' : 'Expand chain'}
                        >
                          {isExpanded ? <IoChevronDown /> : <IoChevronForward />}
                        </ChevronButton>
                      </TableCell>
                      {!hideDate && (
                        <TableCell>
                          <MutedText>{row.date}</MutedText>
                        </TableCell>
                      )}
                      {includeTicker && (
                        <TableCell>
                          <Link href={`${TICKER_FLOW_PAGE}/${row.ticker}`}>
                            <CallPutBackground type={row.cp}>{row.ticker}</CallPutBackground>
                          </Link>
                        </TableCell>
                      )}
                      <TableCell>
                        <CallPutText type={row.cp}>{row.chainLabel}</CallPutText>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-400">{row.count}</span>
                      </TableCell>
                      <TableCell>${row.avgSpot?.toFixed(2) ?? '—'}</TableCell>
                      <TableCell>{Math.round(row.avgOtmPercent)}%</TableCell>
                      {!includeTicker && <TableCell>${row.avgPrice?.toFixed(2) ?? '—'}</TableCell>}
                      <TableCell>{displaySize.toLocaleString()}</TableCell>
                      <TableCell>{row.openInterest.toLocaleString()}</TableCell>
                      <TableCell>{Math.round(row.avgImpliedVol * 100)}%</TableCell>
                      <TableCell>
                        <CallPutText type={row.cp}>${abbreviateNumber(displayPremium)}</CallPutText>
                      </TableCell>
                      <TableCellNoPad>
                        <HeatScoreBadge scoreWidth={row.avgHeatScore} />
                      </TableCellNoPad>
                    </ChainDashboardRow>

                    {isExpanded && (
                      <ChildTableWrapper>
                        <ChildFlowHeader />
                        {row.trades.map((trade) => (
                          <ChildFlowRow key={trade.id} trade={trade} />
                        ))}
                      </ChildTableWrapper>
                    )}
                  </div>
                );
              }}
            />
          ) : isLoading === false ? (
            <div className="flex h-full w-full items-center justify-center text-sm text-gray-400">
              No trades available in the selected range.
            </div>
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Pulse />
            </div>
          )}
        </div>
      </TableBody>
    </div>
  );
}
